/**
 * 标定参数-批量读取结果展示表单
 */
import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Collapse, Row, Col } from 'antd';
import { formSearchGutter } from '@/constants/common';
import { SubDeviceTypeEnum } from '@/constants/deviceManage';

const { Panel } = Collapse;

interface CalibrationParamsReadFormProps {
  data?: any; // 读取到的数据
}

const CalibrationParamsReadForm: React.FC<CalibrationParamsReadFormProps> = ({ data }) => {
  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);

  // 设备ID -> 标定类型映射（与 CalibrationParamsQueryForm 中的 calibrationTypes 对应）
  const deviceIdToType: Record<string | number, string> = {
    [SubDeviceTypeEnum.forwardIntegratedMachine_duolaimu]: 'camera_dlm',
    [SubDeviceTypeEnum.forwardIntegratedMachine_camera_yuanzhiyun]: 'camera_yzy',
    [SubDeviceTypeEnum.forwardIntegratedMachine_radar_yuanzhiyun]: 'radar_yzy',
    [SubDeviceTypeEnum.wireMotor]: 'motor_zdw',
    [SubDeviceTypeEnum.ins_aqi]: 'ins_aqi',
    [SubDeviceTypeEnum.ax_forward_camera]: 'ax_forward_camera',
    [SubDeviceTypeEnum.ax_forward_mmw_radar]: 'ax_forward_mmw_radar',
    [SubDeviceTypeEnum.ax_dms_camera]: 'ax_dms_camera',
    [SubDeviceTypeEnum.ax_bsd_camera]: 'ax_bsd_camera'
  };

  // 当数据变化时设置表单值并根据 resultData.id 确定要显示的标定类别
  useEffect(() => {
    const resultDataStr = data?.resultData || '';
    const resultData = resultDataStr ? JSON.parse(resultDataStr) : {};
    console.log('CalibrationParamsReadForm - resultData:', resultData);
    form.setFieldsValue(resultData);

    const id = resultData?.id;
    const type = id !== undefined && id !== null ? deviceIdToType[id] : undefined;
    setSelectedType(type);
  }, [data, form]);

  return (
    <Form form={form} layout="vertical">
      {/* 若无法识别 selectedType，则显示占位文字 */}
      {!selectedType ? (
        <div style={{ padding: 16, textAlign: 'center' }}>无法获取标定数据</div>
      ) : (
        <Collapse activeKey={[selectedType]}>
          {selectedType === 'camera_dlm' && (
            <Panel header="前向一体机标定结果(多来目)" key="camera_dlm">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="镜头高度(米)"
                    name="cameraMountingHeight"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="镜头高度" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="设备至车头距离(米)"
                    name="frontBumperDistance"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="距离" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="设备至车右侧距离(米)"
                    name="rightSideDistance"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="距离" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="设备至车左侧距离(米)"
                    name="leftSideDistance"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="距离" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'camera_yzy' && (
            <Panel header="相机标定结果(园之云)" key="camera_yzy">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="摄像头到前保的纵向距离(米)"
                    name="cameraFrontBumperDistance"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="距离(负值)" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="摄像头中线距离(米)"
                    name="cameraCenterOffset"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="左正右负" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="摄像头到地面距离(米)"
                    name="cameraHeight"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="高度" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定板图案中心高度(米)"
                    name="calibrationBoardCenterHeight"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="高度" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置x"
                    name="locationX"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定后位置x" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置y"
                    name="locationY"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定后位置y" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置z"
                    name="locationZ"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定后位置z" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后pitch"
                    name="pitch"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定后pitch" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后yaw"
                    name="yaw"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定后yaw" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后roll"
                    name="roll"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定后roll" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'radar_yzy' && (
            <Panel header="雷达标定结果(园之云)" key="radar_yzy">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="后轴到雷达距离(米)"
                    name="xOffset"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="距离" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="雷达中心偏离(米)"
                    name="yOffset"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="左正右负" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="雷达中心离地高度(米)"
                    name="zOffset"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="高度" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'motor_zdw' && (
            <Panel header="拉线电机标定结果" key="motor_zdw">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="C3"
                    name="c3"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="C3" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="C2"
                    name="c2"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="C2" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="C1"
                    name="c1"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="C1" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="C0"
                    name="c0"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="C0" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="静态标定: 空行程"
                    name="pedalIdleTravel"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="静态标定: 空行程" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="静态标定: 最大行程"
                    name="pedalMaxTravel"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="静态标定: 最大行程" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="静态标定-x^1系数"
                    name="staticC1"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="静态标定-x^1系数" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="静态标定-常数项系数"
                    name="staticC0"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="静态标定-常数项系数" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'ins_aqi' && (
            <Panel header="组合惯导标定结果(阿奇)" key="ins_aqi">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'ax_forward_camera' && (
            <Panel header="安行前向相机标定结果" key="ax_forward_camera">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置x"
                    name="locationX"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置x" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置y"
                    name="locationY"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置y" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置z"
                    name="locationZ"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置z" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后pitch"
                    name="pitch"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="pitch" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后yaw"
                    name="yaw"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="yaw" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后roll"
                    name="roll"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="roll" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'ax_forward_mmw_radar' && (
            <Panel header="安行前向毫米波雷达标定结果" key="ax_forward_mmw_radar">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置x"
                    name="locationX"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置x" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置y"
                    name="locationY"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置y" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置z"
                    name="locationZ"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置z" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后pitch"
                    name="pitch"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="pitch" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后yaw"
                    name="yaw"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="yaw" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后roll"
                    name="roll"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="roll" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'ax_dms_camera' && (
            <Panel header="安行DMS相机标定结果" key="ax_dms_camera">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="headPitch"
                    name="headPitch"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="headPitch" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="headYaw"
                    name="headYaw"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="headYaw" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="headRoll"
                    name="headRoll"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="headRoll" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="gazePitch"
                    name="gazePitch"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="gazePitch" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="gazeYaw"
                    name="gazeYaw"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="gazeYaw" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}

          {selectedType === 'ax_bsd_camera' && (
            <Panel header="安行BSD相机标定结果" key="ax_bsd_camera">
              <Row gutter={formSearchGutter}>
                <Col span={8}>
                  <Form.Item
                    label="标定状态"
                    name="success"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="标定状态" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置x"
                    name="locationX"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置x" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置y"
                    name="locationY"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置y" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后位置z"
                    name="locationZ"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="位置z" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后pitch"
                    name="pitch"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="pitch" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后yaw"
                    name="yaw"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="yaw" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="标定后roll"
                    name="roll"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="roll" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="imgW"
                    name="imgW"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="imgW" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="imgH"
                    name="imgH"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="imgH" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="px"
                    name="px"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="px" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="py"
                    name="py"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="py" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="fx"
                    name="fx"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="fx" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="fy"
                    name="fy"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="fy" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="cx"
                    name="cx"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="cx" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="cy"
                    name="cy"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="cy" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="k1"
                    name="k1"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="k1" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="k2"
                    name="k2"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="k2" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="k3"
                    name="k3"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="k3" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="k4"
                    name="k4"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="k4" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="mountMode"
                    name="mountMode"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="mountMode" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="calibrationFieldVersion"
                    name="calibrationFieldVersion"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="calibrationFieldVersion" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="cameraModel"
                    name="cameraModel"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="cameraModel" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="detectionMode"
                    name="detectionMode"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="detectionMode" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="vehicleCoordOrigin"
                    name="vehicleCoordOrigin"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="vehicleCoordOrigin" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="reserved"
                    name="reserved"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <InputNumber disabled style={{ width: '100%' }} placeholder="reserved" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="algorithmVersion"
                    name="algorithmVersion"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <Input disabled style={{ width: '100%' }} placeholder="algorithmVersion" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="pcVersion"
                    name="pcVersion"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <Input disabled style={{ width: '100%' }} placeholder="pcVersion" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="time"
                    name="time"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                  >
                    <Input disabled style={{ width: '100%' }} placeholder="time" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          )}
        </Collapse>
      )}
    </Form>
  );
};

export default CalibrationParamsReadForm;
